import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { usePolling } from "@/lib/usePolling";
import { CoachTopBar } from "@/components/Nav";
import { ArrowLeft, Save, X } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export function CoachDashboard() {
  const [clients, setClients] = useState([]);
  const [alerts, setAlerts] = useState([]);
  usePolling(async () => {
    const [cl, al] = await Promise.all([api.get("/coach/clients"), api.get("/coach/alerts")]);
    setClients(cl.data);
    setAlerts(al.data);
  }, [], 0);
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <CoachTopBar />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="font-display font-black uppercase text-4xl tracking-tighter">Clients</h1>
        <p className="text-neutral-400 text-sm mt-1">{clients.length} active · 12-Week Warrior Transformation</p>

        {alerts.length > 0 && (
          <div className="kw-card p-4 mt-5 border-gold" data-testid="alerts-panel">
            <div className="text-xs uppercase tracking-widest text-gold mb-2">⚠ Alerts — needs your attention</div>
            <div className="space-y-2">
              {alerts.map(a => (
                <Link key={a.client_id} to={`/coach/clients/${a.client_id}`} className="flex justify-between py-1.5 border-b border-[#262626] text-sm hover:text-gold" data-testid={`alert-${a.client_id}`}>
                  <span>{a.name} {a.is_demo && <span className="text-[9px] bg-gold text-black px-1 ml-1">DEMO</span>}</span>
                  <span className="text-neutral-400 text-xs">
                    {a.days_since_checkin >= 999 ? "No check-ins yet" : a.days_since_checkin >= 2 ? `${a.days_since_checkin}d no check-in` : ""}
                    {a.declining_adherence ? " · Discipline declining" : ""}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {clients.map(c => (
            <Link key={c.id} to={`/coach/clients/${c.id}`} className="kw-card p-4 hover:border-gold transition-colors" data-testid={`client-card-${c.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-display font-bold uppercase text-xl">{c.name}</div>
                  <div className="text-xs text-neutral-500">{c.email}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {c.is_demo && <span className="text-[9px] bg-gold text-black px-1.5 py-0.5 uppercase tracking-widest font-bold" data-testid="demo-badge">Demo</span>}
                  {!c.onboarding_complete && <span className="text-[10px] bg-[#262626] px-2 py-0.5 uppercase tracking-widest">Pending</span>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                <div><div className="text-neutral-500 uppercase text-[10px]">Weight</div><div className="font-display text-lg">{c.last_weight?.weight_kg || "-"} kg</div></div>
                <div><div className="text-neutral-500 uppercase text-[10px]">Target</div><div className="font-display text-lg">{c.onboarding?.targets?.calories || "-"} kcal</div></div>
                <div><div className="text-neutral-500 uppercase text-[10px]">Last check-in</div><div className="font-display text-lg">{c.last_checkin?.discipline_score || "-"}/10</div></div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CoachClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [t, setT] = useState({ calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 });
  const [plan, setPlan] = useState({ client_id: id, week: 1, day: 1, title: "", type: "strength", exercises: [], notes: "" });
  const [msg, setMsg] = useState("");
  const [summaryWeek, setSummaryWeek] = useState(1);
  const [summary, setSummary] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [diaryDate, setDiaryDate] = useState(new Date().toISOString().slice(0,10));
  const [diary, setDiary] = useState(null);
  const [entryError, setEntryError] = useState("");
  const [editingEntry, setEditingEntry] = useState(null); // entry id being edited
  const [entryDraft, setEntryDraft] = useState({});
  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState({});
  const [compareIds, setCompareIds] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [photoError, setPhotoError] = useState("");

  const load = async () => {
    const { data } = await api.get(`/coach/clients/${id}`);
    setC(data);
    if (data.onboarding?.targets) setT(data.onboarding.targets);
    const n = await api.get(`/coach/notes?client_id=${id}`);
    setNotes(n.data);
  };
  const loadSummary = async (w) => {
    const { data } = await api.get(`/coach/clients/${id}/weekly-summary?week=${w}`);
    setSummary(data);
    // On first load, snap the picker to the client's current programme week
    if (data?.programme_week && !summaryTouched.current) {
      summaryTouched.current = true;
      if (data.programme_week !== w) setSummaryWeek(data.programme_week);
    }
  };
  const summaryTouched = React.useRef(false);
  usePolling(load, [id], 0);
  usePolling(() => loadSummary(summaryWeek), [summaryWeek, id]);
  usePolling(async () => {
    const { data } = await api.get(`/coach/clients/${id}/food-summary?date=${diaryDate}`);
    setDiary(data);
  }, [id, diaryDate]);

  // Load progress photos list + their blobs
  useEffect(() => {
    let cancelled = false;
    const createdUrls = [];
    const loadPhotos = async () => {
      setPhotoError("");
      try {
        const { data } = await api.get(`/progress/photos?user_id=${id}`);
        if (cancelled) return;
        setPhotos(data);
        const token = localStorage.getItem("kw_token");
        const next = {};
        for (const ph of data) {
          try {
            const resp = await fetch(`${API_BASE}/progress/photo/${ph.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
            if (resp.ok) {
              const blob = await resp.blob();
              const url = URL.createObjectURL(blob);
              createdUrls.push(url);
              next[ph.id] = url;
            }
          } catch {}
        }
        if (!cancelled) setPhotoUrls(next);
      } catch (e) {
        if (!cancelled) setPhotoError(formatApiError(e));
      }
    };
    loadPhotos();
    return () => { cancelled = true; createdUrls.forEach(u => URL.revokeObjectURL(u)); };
  }, [id]);

  const toggleCompare = (pid) => {
    setCompareIds(prev => {
      if (prev.includes(pid)) return prev.filter(x => x !== pid);
      if (prev.length >= 2) return [prev[1], pid];
      return [...prev, pid];
    });
  };

  const programmeStart = c?.created_at ? new Date(c.created_at) : null;
  const programmeWeek = (iso) => {
    if (!programmeStart || !iso) return null;
    const diff = (new Date(iso) - programmeStart) / (1000 * 60 * 60 * 24);
    const w = Math.floor(diff / 7) + 1;
    return w < 1 ? 1 : w;
  };

  const saveTargets = async () => {
    setMsg("");
    try {
      await api.put(`/onboarding/${id}/targets`, { calories: +t.calories, protein_g: +t.protein_g, carbs_g: +t.carbs_g, fat_g: +t.fat_g });
      setMsg("Targets updated");
    } catch (e) { setMsg(formatApiError(e)); }
  };

  const addPlan = async () => {
    setMsg("");
    try {
      await api.post("/workouts/plan", plan);
      setPlan({ ...plan, title: "", notes: "" });
      load();
      setMsg("Plan saved");
    } catch (e) { setMsg(formatApiError(e)); }
  };

  const saveNote = async () => {
    if (!noteDraft.trim()) return;
    await api.post("/coach/notes", { client_id: id, week: summaryWeek, content: noteDraft });
    setNoteDraft("");
    const n = await api.get(`/coach/notes?client_id=${id}`);
    setNotes(n.data);
    setMsg("Feedback note saved");
  };

  const beginEditEntry = (e) => {
    setEntryError("");
    setEditingEntry(e.id);
    setEntryDraft({
      portion_grams: e.portion_grams,
      calories: Math.round(e.calories),
      protein_g: e.protein_g,
      carbs_g: e.carbs_g,
      fat_g: e.fat_g,
    });
  };
  const refreshDiary = async () => {
    const { data } = await api.get(`/coach/clients/${id}/food-summary?date=${diaryDate}`);
    setDiary(data);
  };
  const saveEntryCorrection = async (entryId) => {
    const fields = [["portion_grams", "Portion"], ["calories", "kcal"], ["protein_g", "Protein"], ["carbs_g", "Carbs"], ["fat_g", "Fat"]];
    const payload = {};
    for (const [k, label] of fields) {
      const raw = String(entryDraft[k] ?? "").trim();
      const n = Number(raw);
      if (raw === "" || !Number.isFinite(n) || n < 0 || (k === "portion_grams" && n <= 0)) {
        setEntryError(`${label} needs a valid number${k === "portion_grams" ? " above 0" : " (0 or more)"}. Nothing was saved.`);
        return;
      }
      payload[k] = n;
    }
    setEntryError("");
    try {
      await api.patch(`/coach/clients/${id}/food-log/${entryId}`, payload);
      setEditingEntry(null);
      setEntryDraft({});
      await refreshDiary();
      setMsg("Entry corrected");
    } catch (e) { setEntryError(formatApiError(e)); }
  };

  if (!c) return <div className="min-h-screen bg-[#0A0A0A] text-neutral-500 grid place-items-center">Loading…</div>;

  const weightChart = (c.weights || []).map(w => ({ date: w.date.slice(5), weight: w.weight_kg }));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <CoachTopBar />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <button onClick={()=>nav("/coach")} className="text-neutral-400 text-sm flex items-center gap-1 mb-3" data-testid="back-to-clients"><ArrowLeft className="w-4 h-4"/>Clients</button>
        <h1 className="font-display font-black uppercase text-4xl tracking-tighter">{c.name}</h1>
        <p className="text-neutral-500 text-sm">{c.email}</p>

        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <div className="kw-card p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Onboarding</div>
            {c.onboarding ? (
              <div className="mt-2 text-sm space-y-1">
                <div>Age: <span className="text-gold">{c.onboarding.age}</span> · Sex: {c.onboarding.sex}</div>
                <div>Height: {c.onboarding.height_cm}cm · Weight: {c.onboarding.weight_kg}kg · Goal: {c.onboarding.goal_weight_kg||"-"}kg</div>
                <div>Activity: {c.onboarding.activity_level} · Goal: <span className="text-gold">{c.onboarding.goal}</span></div>
                <div>Experience: {c.onboarding.exercise_experience} · IF: {c.onboarding.intermittent_fasting?"yes":"no"}</div>
                <div>Equipment: {(c.onboarding.equipment||[]).join(", ") || "none"}</div>
                <div>Medical: {c.onboarding.medical_info || "—"} {c.onboarding.medical_clearance_needed && <span className="text-gold">· GP clearance flagged</span>}</div>
                <div>Food prefs: {c.onboarding.food_preferences || "—"}</div>
              </div>
            ) : <div className="text-sm text-neutral-500 mt-2">Not completed.</div>}
          </div>

          <div className="kw-card p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Adjust targets</div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {["calories","protein_g","carbs_g","fat_g"].map(k => (
                <div key={k}>
                  <label className="kw-label">{k.replace("_g","")}</label>
                  <input className="kw-input" type="number" value={t[k]||0} onChange={e=>setT({...t,[k]:e.target.value})} data-testid={`target-${k}`}/>
                </div>
              ))}
            </div>
            <button onClick={saveTargets} className="kw-btn-primary w-full mt-3 flex items-center justify-center gap-1" data-testid="save-targets"><Save className="w-4 h-4"/>Save</button>
          </div>
        </div>

        <div className="kw-card p-4 mt-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Weight trend</div>
          <div className="h-48 mt-2">
            <ResponsiveContainer>
              <LineChart data={weightChart}>
                <CartesianGrid stroke="#262626" strokeDasharray="2 3"/>
                <XAxis dataKey="date" stroke="#737373" fontSize={10}/>
                <YAxis stroke="#737373" fontSize={10} domain={["dataMin - 2","dataMax + 2"]}/>
                <Tooltip contentStyle={{background:"#171717",border:"1px solid #333",fontSize:12}}/>
                <Line type="monotone" dataKey="weight" stroke="#D4AF37" strokeWidth={2}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Progress photos (coach view) */}
        <div className="kw-card p-4 mt-4" data-testid="coach-photos-panel">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-gold">Progress photos</div>
              <div className="text-[11px] text-neutral-500 mt-1">
                {photos.length} photo{photos.length===1?"":"s"} · Tap to view · Tick up to 2 to compare across weeks
              </div>
            </div>
            {compareIds.length === 2 && (
              <button className="kw-btn-primary text-xs" onClick={()=>setLightbox({ compare: true })} data-testid="coach-photos-compare">
                Compare selected
              </button>
            )}
          </div>
          {photoError && <div className="text-red-400 text-sm mt-2" data-testid="coach-photos-error">{photoError}</div>}
          {photos.length === 0 ? (
            <div className="text-sm text-neutral-500 text-center py-6" data-testid="coach-photos-empty">No photos uploaded by this client yet.</div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mt-3">
              {photos.map(p => {
                const wk = programmeWeek(p.created_at);
                const selected = compareIds.includes(p.id);
                return (
                  <div key={p.id} className={`relative aspect-[3/4] bg-[#171717] border overflow-hidden ${selected?"border-gold":"border-[#262626]"}`} data-testid={`coach-photo-${p.id}`}>
                    {photoUrls[p.id] ? (
                      <img
                        src={photoUrls[p.id]}
                        alt={`progress ${p.created_at}`}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={()=>setLightbox({ compare: false, id: p.id })}
                        data-testid={`coach-photo-img-${p.id}`}
                      />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-xs text-neutral-500">Loading…</div>
                    )}
                    <label className="absolute top-1 right-1 bg-black/70 border border-[#333] text-[10px] px-1.5 py-0.5 uppercase tracking-widest flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        className="accent-gold w-3 h-3"
                        checked={selected}
                        onChange={()=>toggleCompare(p.id)}
                        data-testid={`coach-photo-compare-${p.id}`}
                      />
                      <span className={selected?"text-gold":"text-neutral-400"}>pick</span>
                    </label>
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 py-1">
                      <div className="text-[10px] uppercase tracking-widest text-gold" data-testid={`coach-photo-week-${p.id}`}>{wk ? `Week ${wk}` : "—"}</div>
                      <div className="text-[10px] text-neutral-300" data-testid={`coach-photo-date-${p.id}`}>{p.created_at ? new Date(p.created_at).toLocaleDateString() : ""}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-neutral-500 mt-3">Private — only this client and you can view these photos.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="kw-card p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Recent check-ins</div>
            <div className="mt-2 space-y-2">
              {c.checkins.slice(0,6).map(ci => (
                <div key={ci.id} className="border border-[#262626] p-2 text-sm">
                  <div className="flex justify-between"><span>{ci.date}</span><span className="text-gold">{ci.discipline_score}/10</span></div>
                  <div className="text-xs text-neutral-500">Nut {ci.nutrition_on_track?"✓":"✗"} · Train {ci.workout_done?"✓":"✗"} · {ci.sleep_hours}h sleep · {ci.water_litres}L</div>
                  {ci.notes && <div className="text-xs text-neutral-400 mt-1">{ci.notes}</div>}
                </div>
              ))}
              {c.checkins.length===0 && <div className="text-sm text-neutral-500">No check-ins yet.</div>}
            </div>
          </div>

          <div className="kw-card p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Assign plan</div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <input className="kw-input" type="number" min="1" max="12" value={plan.week} onChange={e=>setPlan({...plan,week:+e.target.value})} placeholder="Week" data-testid="plan-week"/>
              <input className="kw-input" type="number" min="1" max="7" value={plan.day} onChange={e=>setPlan({...plan,day:+e.target.value})} placeholder="Day" data-testid="plan-day"/>
              <select className="kw-input" value={plan.type} onChange={e=>setPlan({...plan,type:e.target.value})} data-testid="plan-type">
                <option value="strength">Strength</option><option value="cardio">Cardio</option>
                <option value="walking">Walking</option><option value="recovery">Recovery</option>
              </select>
            </div>
            <input className="kw-input mt-2" placeholder="Title" value={plan.title} onChange={e=>setPlan({...plan,title:e.target.value})} data-testid="plan-title"/>
            <textarea className="kw-input mt-2" rows={2} placeholder="Notes" value={plan.notes} onChange={e=>setPlan({...plan,notes:e.target.value})} data-testid="plan-notes"/>
            <button onClick={addPlan} className="kw-btn-primary w-full mt-3" data-testid="plan-save">Save plan</button>
            <div className="mt-3 text-xs text-neutral-500">Existing: {c.plans.length} entries</div>
          </div>
        </div>

        {msg && <div className="mt-4 text-gold text-sm" data-testid="coach-msg">{msg}</div>}

        {/* Daily Food Diary */}
        <div className="kw-card p-4 mt-4" data-testid="food-diary">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-gold">Daily Food Diary</div>
              <div className="text-[11px] text-neutral-500 mt-1">Live view — refreshes every 15s. Reads the client's own saved entries.</div>
            </div>
            <input type="date" className="kw-input w-auto" value={diaryDate} onChange={e=>setDiaryDate(e.target.value)} data-testid="diary-date"/>
          </div>
          {diary && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                <Stat label="Calories" value={`${Math.round(diary.totals.calories)} / ${diary.targets.calories}`}/>
                <Stat label="Kcal remaining" value={diary.remaining.calories > 0 ? `${Math.round(diary.remaining.calories)}` : `+${Math.abs(Math.round(diary.remaining.calories))} over`}/>
                <Stat label="Protein" value={`${Math.round(diary.totals.protein_g)}g / ${diary.targets.protein_g}g`}/>
                <Stat label="Protein remaining" value={diary.remaining.protein_g > 0 ? `${Math.round(diary.remaining.protein_g)}g` : "✓ hit"}/>
                <Stat label="Carbs" value={`${Math.round(diary.totals.carbs_g)}g / ${diary.targets.carbs_g}g`}/>
                <Stat label="Carbs remaining" value={`${Math.round(diary.remaining.carbs_g)}g`}/>
                <Stat label="Fat" value={`${Math.round(diary.totals.fat_g)}g / ${diary.targets.fat_g}g`}/>
                <Stat label="Fat remaining" value={`${Math.round(diary.remaining.fat_g)}g`}/>
              </div>
              <div className="mt-4 space-y-3">
                {["breakfast","lunch","dinner","snack"].map(m => (
                  <div key={m} className="border border-[#262626] p-3" data-testid={`diary-meal-${m}`}>
                    <div className="flex justify-between items-center">
                      <div className="text-[10px] uppercase tracking-widest text-gold">{m}</div>
                      <div className="text-[10px] text-neutral-500">{diary.by_meal[m]?.length || 0} item{(diary.by_meal[m]?.length||0)===1?"":"s"}</div>
                    </div>
                    {(diary.by_meal[m]||[]).length === 0 ? (
                      <div className="text-xs text-neutral-500 mt-1 italic">Nothing logged</div>
                    ) : (
                      <table className="w-full text-xs mt-2">
                        <thead className="text-neutral-500">
                          <tr><th className="text-left py-1">Food</th><th className="text-right">g</th><th className="text-right">kcal</th><th className="text-right">P</th><th className="text-right">C</th><th className="text-right">F</th><th className="text-right"></th></tr>
                        </thead>
                        <tbody>
                          {diary.by_meal[m].map(e => (
                            editingEntry === e.id ? (
                              <tr key={e.id} className="border-t border-[#262626] bg-[#141414]" data-testid={`diary-edit-row-${e.id}`}>
                                <td colSpan={7} className="py-3">
                                  <div className="text-sm text-neutral-200 mb-2">{e.food_name}</div>
                                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                    <label className="block">
                                      <span className="text-[10px] uppercase tracking-widest text-neutral-500">Portion (g)</span>
                                      <input type="number" step="1" min="0" className="kw-input w-full h-11 min-h-[44px]" value={entryDraft.portion_grams} onChange={ev=>setEntryDraft({...entryDraft, portion_grams: ev.target.value})} data-testid={`diary-edit-portion-${e.id}`}/>
                                    </label>
                                    <label className="block">
                                      <span className="text-[10px] uppercase tracking-widest text-neutral-500">kcal</span>
                                      <input type="number" step="1" min="0" className="kw-input w-full h-11 min-h-[44px]" value={entryDraft.calories} onChange={ev=>setEntryDraft({...entryDraft, calories: ev.target.value})} data-testid={`diary-edit-kcal-${e.id}`}/>
                                    </label>
                                    <label className="block">
                                      <span className="text-[10px] uppercase tracking-widest text-neutral-500">Protein (g)</span>
                                      <input type="number" step="0.1" min="0" className="kw-input w-full h-11 min-h-[44px]" value={entryDraft.protein_g} onChange={ev=>setEntryDraft({...entryDraft, protein_g: ev.target.value})} data-testid={`diary-edit-p-${e.id}`}/>
                                    </label>
                                    <label className="block">
                                      <span className="text-[10px] uppercase tracking-widest text-neutral-500">Carbs (g)</span>
                                      <input type="number" step="0.1" min="0" className="kw-input w-full h-11 min-h-[44px]" value={entryDraft.carbs_g} onChange={ev=>setEntryDraft({...entryDraft, carbs_g: ev.target.value})} data-testid={`diary-edit-c-${e.id}`}/>
                                    </label>
                                    <label className="block">
                                      <span className="text-[10px] uppercase tracking-widest text-neutral-500">Fat (g)</span>
                                      <input type="number" step="0.1" min="0" className="kw-input w-full h-11 min-h-[44px]" value={entryDraft.fat_g} onChange={ev=>setEntryDraft({...entryDraft, fat_g: ev.target.value})} data-testid={`diary-edit-f-${e.id}`}/>
                                    </label>
                                  </div>
                                  {entryError && <div className="mt-3 text-red-400 text-sm" role="alert" data-testid={`diary-edit-error-${e.id}`}>{entryError}</div>}
                                  <div className="flex gap-2 mt-3">
                                    <button className="kw-btn-primary text-xs px-4 py-2 min-h-[44px]" onClick={()=>saveEntryCorrection(e.id)} data-testid={`diary-edit-save-${e.id}`}>Save correction</button>
                                    <button className="kw-btn-outline text-xs px-4 py-2 min-h-[44px]" onClick={()=>{setEditingEntry(null); setEntryDraft({}); setEntryError("");}} data-testid={`diary-edit-cancel-${e.id}`}>Cancel</button>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              <tr key={e.id} className="border-t border-[#262626]" data-testid={`diary-row-${e.id}`}>
                                <td className="py-1">{e.food_name}{e.edited_by_coach_at && <span className="text-[9px] text-gold uppercase tracking-widest ml-1" data-testid={`diary-corrected-${e.id}`}>· corrected</span>}</td>
                                <td className="text-right">{e.portion_grams}</td>
                                <td className="text-right">{Math.round(e.calories)}</td>
                                <td className="text-right">{e.protein_g}</td>
                                <td className="text-right">{e.carbs_g}</td>
                                <td className="text-right">{e.fat_g}</td>
                                <td className="text-right"><button className="text-neutral-400 hover:text-gold uppercase tracking-widest text-[10px] px-2 py-1" onClick={()=>beginEditEntry(e)} data-testid={`diary-edit-${e.id}`}>Edit</button></td>
                              </tr>
                            )
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
              {diary.entry_count === 0 && <div className="text-sm text-neutral-500 mt-3 text-center">No meals logged on this date.</div>}
            </>
          )}
        </div>

        {/* Weekly summary & coach notes */}
        <div className="kw-card p-4 mt-4" data-testid="weekly-summary">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs uppercase tracking-widest text-gold">Weekly summary</div>
            <select className="kw-input w-auto" value={summaryWeek} onChange={e=>setSummaryWeek(+e.target.value)} data-testid="summary-week">
              {Array.from({length:12}).map((_,i)=><option key={i+1} value={i+1}>Week {i+1}</option>)}
            </select>
          </div>
          {summary && (
            <>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <Stat label="Check-ins" value={`${summary.checkin_count}/7`}/>
              <Stat label="Workouts" value={summary.workouts_count}/>
              <Stat label="Avg discipline" value={summary.avg_discipline ?? "—"}/>
              <Stat label="Avg energy" value={summary.avg_energy ?? "—"}/>
              <Stat label="Avg sleep" value={summary.avg_sleep ? `${summary.avg_sleep}h` : "—"}/>
              <Stat label="Avg water" value={summary.avg_water ? `${summary.avg_water}L` : "—"}/>
              <Stat label="Avg kcal/day" value={summary.avg_calories ?? "—"}/>
              <Stat label="Weight change" value={summary.weight_change !== null ? `${summary.weight_change > 0 ? "+" : ""}${summary.weight_change}kg` : "—"}/>
            </div>
            <div className="mt-3 text-[11px] text-neutral-500">Programme starts {summary.programme_start} · Currently in week {summary.programme_week} · Range: {summary.start} → {summary.end}</div>
            {summary.workouts_count > 0 && (
              <div className="mt-3 border border-[#262626] p-3" data-testid="workout-breakdown">
                <div className="text-[10px] uppercase tracking-widest text-gold">Workout breakdown · {summary.workouts_total_minutes} min · {summary.workouts_total_steps.toLocaleString()} steps</div>
                <div className="flex flex-wrap gap-2 mt-2 text-xs">
                  {Object.entries(summary.workouts_by_type).map(([t,n])=>(
                    <span key={t} className="border border-[#333] px-2 py-0.5 uppercase tracking-widest text-[10px]">{t}: <span className="text-gold">{n}</span></span>
                  ))}
                </div>
                <table className="w-full text-xs mt-2">
                  <thead className="text-neutral-500"><tr><th className="text-left py-1">Date</th><th className="text-left">Type</th><th className="text-left">Title</th><th className="text-right">Min</th><th className="text-right">Steps</th></tr></thead>
                  <tbody>
                    {summary.workouts_list.map(w => (
                      <tr key={w.id} className="border-t border-[#262626]"><td className="py-1">{w.date}</td><td>{w.type}</td><td>{w.title}</td><td className="text-right">{w.duration_min||0}</td><td className="text-right">{(w.steps||0).toLocaleString()}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </>
          )}
          <div className="mt-4 border-t border-[#262626] pt-3">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Private feedback for week {summaryWeek}</div>
            {notes.filter(n=>n.week===summaryWeek).map(n => (
              <div key={n.id} className="text-sm bg-[#171717] border border-[#262626] p-2 mb-2" data-testid={`note-${n.id}`}>
                {n.content}
                <div className="text-[10px] text-neutral-500 mt-1">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            ))}
            <textarea className="kw-input" rows={2} placeholder="Encouragement, next-week focus, flags…" value={noteDraft} onChange={e=>setNoteDraft(e.target.value)} data-testid="note-input"/>
            <button className="kw-btn-primary mt-2 text-xs" onClick={saveNote} data-testid="note-save">Save feedback for week {summaryWeek}</button>
          </div>
        </div>

        {lightbox && (
          <div
            className="fixed inset-0 bg-black/90 z-50 grid place-items-center p-3 overflow-y-auto"
            onClick={()=>setLightbox(null)}
            data-testid="coach-photo-lightbox"
          >
            <button
              className="absolute top-3 right-3 bg-black/60 border border-[#333] p-2 text-white"
              onClick={(e)=>{e.stopPropagation(); setLightbox(null);}}
              data-testid="coach-photo-lightbox-close"
              aria-label="Close"
            >
              <X className="w-4 h-4"/>
            </button>
            {lightbox.compare ? (
              <div className="grid grid-cols-2 gap-2 max-w-5xl w-full" onClick={(e)=>e.stopPropagation()} data-testid="coach-photo-compare-view">
                {compareIds.map(pid => {
                  const ph = photos.find(x=>x.id===pid);
                  const wk = ph ? programmeWeek(ph.created_at) : null;
                  return (
                    <div key={pid} className="bg-[#0A0A0A] border border-[#262626]">
                      {photoUrls[pid] ? (
                        <img src={photoUrls[pid]} alt="progress" className="w-full h-auto max-h-[75vh] object-contain bg-black" data-testid={`coach-compare-img-${pid}`}/>
                      ) : (
                        <div className="aspect-[3/4] grid place-items-center text-xs text-neutral-500">Loading…</div>
                      )}
                      <div className="px-2 py-1.5 border-t border-[#262626]">
                        <div className="text-[10px] uppercase tracking-widest text-gold">{wk ? `Week ${wk}` : "—"}</div>
                        <div className="text-[11px] text-neutral-300">{ph?.created_at ? new Date(ph.created_at).toLocaleDateString() : ""}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              (() => {
                const ph = photos.find(x=>x.id===lightbox.id);
                const wk = ph ? programmeWeek(ph.created_at) : null;
                return (
                  <div className="max-w-3xl w-full" onClick={(e)=>e.stopPropagation()}>
                    {photoUrls[lightbox.id] && <img src={photoUrls[lightbox.id]} alt="progress" className="w-full h-auto max-h-[85vh] object-contain bg-black" data-testid="coach-photo-single-img"/>}
                    <div className="mt-2 text-center">
                      <div className="text-xs uppercase tracking-widest text-gold">{wk ? `Week ${wk}` : ""}</div>
                      <div className="text-xs text-neutral-300">{ph?.created_at ? new Date(ph.created_at).toLocaleString() : ""}</div>
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[#262626] p-2">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-display font-black text-xl text-white mt-0.5">{value}</div>
    </div>
  );
}

export function CoachApplications() {
  const [apps, setApps] = useState([]);
  const [inviteLinks, setInviteLinks] = useState({});
  const [err, setErr] = useState("");
  const load = () => api.get("/applications").then(r=>setApps(r.data));
  useEffect(() => { load(); }, []);
  const approve = async (id) => {
    setErr("");
    try {
      const { data } = await api.post(`/applications/${id}/approve`);
      setInviteLinks(l => ({ ...l, [id]: data.invite_url, [`${id}_email`]: data.email_sent, [`${id}_resend`]: data.resend_configured }));
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };
  const resendInvite = async (id) => {
    setErr("");
    try {
      const { data } = await api.post(`/applications/${id}/resend-invite`);
      setInviteLinks(l => ({ ...l, [id]: data.invite_url, [`${id}_copied`]: false, [`${id}_email`]: data.email_sent, [`${id}_resend`]: data.resend_configured }));
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };
  const reject = async (id) => {
    if (!window.confirm("Reject this application?")) return;
    try { await api.post(`/applications/${id}/reject`); load(); } catch (e) { setErr(formatApiError(e)); }
  };
  const copy = (url, id) => { navigator.clipboard.writeText(url); setInviteLinks(l => ({ ...l, [`${id}_copied`]: true })); };
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <CoachTopBar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="font-display font-black uppercase text-4xl tracking-tighter">Applications</h1>
        <p className="text-neutral-400 text-sm mt-1">Approve to generate a secure one-time invite link. Send it to the applicant — they create their own password.</p>
        {err && <div className="text-red-400 text-sm mt-3">{err}</div>}
        <div className="mt-6 space-y-3">
          {apps.map(a => (
            <div key={a.id} className="kw-card p-4" data-testid={`app-${a.id}`}>
              <div className="flex justify-between flex-wrap gap-2">
                <div>
                  <div className="font-display font-bold text-xl">{a.name}</div>
                  <div className="text-xs text-neutral-500">{a.email} · {a.phone || "no phone"} · age {a.age || "-"}</div>
                </div>
                <div className={`text-[10px] uppercase tracking-widest ${a.status==="rejected"?"text-red-400":a.status==="invited"?"text-gold":a.status==="approved"?"text-green-400":"text-neutral-400"}`}>{a.status}</div>
              </div>
              <div className="text-sm text-neutral-300 mt-2"><span className="text-neutral-500">Goals:</span> {a.goals}</div>
              {a.message && <div className="text-sm text-neutral-400 mt-1">{a.message}</div>}
              <div className="text-[11px] text-neutral-500 mt-2">{new Date(a.created_at).toLocaleString()}</div>
              {(a.status === "new" || !a.status) && (
                <div className="flex gap-2 mt-3">
                  <button className="kw-btn-primary text-xs" onClick={()=>approve(a.id)} data-testid={`approve-${a.id}`}>Approve & invite</button>
                  <button className="kw-btn-outline text-xs" onClick={()=>reject(a.id)} data-testid={`reject-${a.id}`}>Reject</button>
                </div>
              )}
              {a.status === "invited" && !inviteLinks[a.id] && (
                <div className="flex gap-2 mt-3 items-center">
                  <button className="kw-btn-primary text-xs" onClick={()=>resendInvite(a.id)} data-testid={`resend-${a.id}`}>Generate fresh invite link</button>
                  <span className="text-[11px] text-neutral-500">Previous link will be revoked</span>
                </div>
              )}
              {inviteLinks[a.id] && (
                <div className="mt-3 border border-gold p-3 bg-[#171717]">
                  <div className="text-[10px] uppercase tracking-widest text-gold">One-time invite link for {a.email}:</div>
                  <div className="font-mono text-xs break-all mt-1 select-all" data-testid={`invite-link-${a.id}`}>{inviteLinks[a.id]}</div>
                  <div className="flex gap-2 mt-2 items-center flex-wrap">
                    <button className="kw-btn-outline text-xs" onClick={()=>copy(inviteLinks[a.id], a.id)} data-testid={`copy-${a.id}`}>{inviteLinks[`${a.id}_copied`]?"Copied ✓":"Copy link"}</button>
                    <button className="kw-btn-outline text-xs" onClick={()=>resendInvite(a.id)} data-testid={`regen-${a.id}`}>Regenerate</button>
                    <a className="kw-btn-outline text-xs" target="_blank" rel="noreferrer" href={`https://wa.me/?text=${encodeURIComponent("Kia ora " + a.name + ", your Kaihau Warrior Coaching invite: " + inviteLinks[a.id])}`} data-testid={`wa-${a.id}`}>Send via WhatsApp</a>
                    {inviteLinks[`${a.id}_email`] && <span className="text-xs text-gold">· Emailed via Resend ✓</span>}
                    {inviteLinks[`${a.id}_resend`] === false && <span className="text-[11px] text-neutral-500">· Resend not configured — share manually</span>}
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-2">Expires in 14 days · Single use · Only works for {a.email}</div>
                </div>
              )}
            </div>
          ))}
          {apps.length===0 && <div className="text-neutral-500 text-sm">No applications yet.</div>}
        </div>
      </div>
    </div>
  );
}
